/**
 * PublicScreenManager — full-screen moderator workspace for public screens.
 *
 * Route: /sim/:id/public-screen-manager (moderator only)
 * Layout: Top (controls: Screen A left + Screen B right) + Bottom (preview)
 *
 * SPEC: MODULES/M8_PUBLIC_SCREEN/SPEC_PUBLIC_SCREEN_SPRINT.md (Section 6)
 */

import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { ScreenBEditor } from '@/components/facilitator/ScreenBEditor'
import { SpeechRecorder } from '@/components/facilitator/SpeechRecorder'
// ReviewRoundResults moved to standalone page /sim/:id/review-results

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface WiDraft {
  round_num: number
  status: string
  geopolitical_tension: number
  economic_crisis: number
  doomsday_clock: number
  distance_to_agi: number
  outlook_tension: string
  outlook_crisis: string
  outlook_doom: string
  outlook_agi: string
  power_columbia: number
  power_cathay: number
}

const API_BASE = import.meta.env.VITE_API_URL ?? ''

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

async function authFetch(url: string, opts: RequestInit = {}): Promise<Response> {
  const token = await getToken()
  return fetch(`${API_BASE}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((opts.headers as Record<string, string>) || {}),
    },
  })
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function PublicScreenManager() {
  const { id: simId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [simName, setSimName] = useState('')
  const [currentRound, setCurrentRound] = useState(0)
  const [currentPhase, setCurrentPhase] = useState('')

  // Preview: 'none' | 'a' | 'b'
  const [previewScreen, setPreviewScreen] = useState<'none' | 'a' | 'b'>('none')


  // World indicators
  const [wiDraft, setWiDraft] = useState<WiDraft | null>(null)
  const [wiLoading, setWiLoading] = useState(false)

  // Broadcast
  const [broadcastText, setBroadcastText] = useState('')

  /* ── Load sim state ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!simId) return
    const load = async () => {
      const { data } = await supabase.from('sim_runs')
        .select('name,current_round,current_phase')
        .eq('id', simId).single()
      if (data) {
        setSimName((data.name as string) || simId.slice(0, 8))
        setCurrentRound(data.current_round as number)
        setCurrentPhase(data.current_phase as string)
      }
    }
    load()
    const ch = supabase.channel(`psm-sim-${simId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sim_runs', filter: `id=eq.${simId}` }, () => load())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId])

  /* ── World indicators ────────────────────────────────────────────── */
  const loadWiDraft = useCallback(async () => {
    if (!simId) return
    try {
      const res = await authFetch(`/api/sim/${simId}/world-indicators/draft`)
      if (!res.ok) return
      const json = await res.json()
      const d = json.data
      if (!d) return
      const gt = d.geopolitical_tension || {}
      const ec = d.economic_crisis || {}
      const dc = d.doomsday_clock || {}
      const da = d.distance_to_agi || {}
      setWiDraft({
        geopolitical_tension: gt.level ?? 5,
        economic_crisis: ec.level ?? 3,
        doomsday_clock: dc.level ?? 3,
        distance_to_agi: da.level ?? 2,
        outlook_tension: gt.outlook ?? 'stable',
        outlook_crisis: ec.outlook ?? 'stable',
        outlook_doom: dc.outlook ?? 'stable',
        outlook_agi: da.outlook ?? 'stable',
        power_columbia: d.power_index_columbia ?? 70,
        power_cathay: d.power_index_cathay ?? 50,
        status: d.status ?? 'draft',
        round_num: d.round_num ?? 0,
      })
    } catch { /* graceful */ }
  }, [simId])

  useEffect(() => {
    loadWiDraft()
    const ch = supabase.channel(`psm-wi-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'world_indicators', filter: `sim_run_id=eq.${simId}` }, () => loadWiDraft())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, loadWiDraft])

  const handleWiGenerate = async () => {
    if (!simId) return
    setWiLoading(true)
    try {
      const res = await authFetch(`/api/sim/${simId}/world-indicators/update`, { method: 'POST' })
      if (!res.ok) {
        const text = await res.text()
        try { const j = JSON.parse(text); alert(j.detail || 'LLM generation failed') }
        catch { alert(`LLM generation failed: ${text.slice(0, 100)}`) }
      }
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
    finally { setWiLoading(false) }
  }

  const handleWiPublish = async () => {
    if (!simId || !wiDraft) return
    setWiLoading(true)
    try {
      const body = {
        geopolitical_tension: { level: wiDraft.geopolitical_tension, outlook: wiDraft.outlook_tension },
        economic_crisis: { level: wiDraft.economic_crisis, outlook: wiDraft.outlook_crisis },
        doomsday_clock: { level: wiDraft.doomsday_clock, outlook: wiDraft.outlook_doom },
        distance_to_agi: { level: wiDraft.distance_to_agi, outlook: wiDraft.outlook_agi },
        power_index_columbia: wiDraft.power_columbia,
        power_index_cathay: wiDraft.power_cathay,
      }
      const res = await authFetch(`/api/sim/${simId}/world-indicators/publish`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const text = await res.text()
        try { const j = JSON.parse(text); alert(j.detail || 'Publish failed') }
        catch { alert(`Publish failed: ${text.slice(0, 100)}`) }
      }
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
    finally { setWiLoading(false) }
  }

  const handleWiSave = async (field: string, value: number | string) => {
    if (!wiDraft || !simId) return
    // Build the new draft FIRST, then use it both for local state and the DB payload.
    // The level fields (geopolitical_tension, economic_crisis, doomsday_clock,
    // distance_to_agi) are JSONB columns of shape { level, outlook } — we have to
    // write the full object so the opposite field is preserved. The outlook fields
    // (outlook_tension, etc.) are NOT separate columns; they live inside the same
    // JSONB. The power fields map to separate integer columns.
    const newDraft: WiDraft = { ...wiDraft, [field]: value }
    setWiDraft(newDraft)
    type Updatable = {
      geopolitical_tension?: { level: number; outlook: string }
      economic_crisis?: { level: number; outlook: string }
      doomsday_clock?: { level: number; outlook: string }
      distance_to_agi?: { level: number; outlook: string }
      power_index_columbia?: number
      power_index_cathay?: number
    }
    let updatePayload: Updatable = {}
    switch (field) {
      case 'geopolitical_tension':
      case 'outlook_tension':
        updatePayload = { geopolitical_tension: { level: newDraft.geopolitical_tension, outlook: newDraft.outlook_tension } }
        break
      case 'economic_crisis':
      case 'outlook_crisis':
        updatePayload = { economic_crisis: { level: newDraft.economic_crisis, outlook: newDraft.outlook_crisis } }
        break
      case 'doomsday_clock':
      case 'outlook_doom':
        updatePayload = { doomsday_clock: { level: newDraft.doomsday_clock, outlook: newDraft.outlook_doom } }
        break
      case 'distance_to_agi':
      case 'outlook_agi':
        updatePayload = { distance_to_agi: { level: newDraft.distance_to_agi, outlook: newDraft.outlook_agi } }
        break
      case 'power_columbia':
        updatePayload = { power_index_columbia: newDraft.power_columbia }
        break
      case 'power_cathay':
        updatePayload = { power_index_cathay: newDraft.power_cathay }
        break
      default:
        return
    }
    try {
      const { error } = await supabase.from('world_indicators')
        .update(updatePayload)
        .eq('sim_run_id', simId)
        .eq('round_num', wiDraft.round_num)
      if (error) console.warn('[world-indicators] save failed', error)
    } catch (e) { console.warn('[world-indicators] save threw', e) }
  }

  const handleBroadcast = async () => {
    if (!broadcastText.trim() || !simId) return
    try {
      await supabase.from('public_broadcasts').insert({
        sim_run_id: simId,
        content: broadcastText.trim(),
      })
      setBroadcastText('')
    } catch (e) { console.error('Broadcast failed:', e) }
  }

  if (!simId) return null

  return (
    <div className="h-screen bg-base flex flex-col overflow-hidden">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-5 py-2 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="font-body text-caption text-text-secondary hover:text-text-primary">&larr; Back</button>
          <h1 className="font-heading text-h3 text-text-primary">Public Screen Manager</h1>
          <span className="font-data text-caption text-text-secondary">{simName} · R{currentRound} · Phase {currentPhase}</span>
        </div>
      </header>

      {/* ── Management area: Screen A controls (left) + Screen B editor (right) */}
      <div className="flex-1 flex min-h-0 overflow-hidden">

        {/* ── Left: Screen A controls ──────────────────────────────── */}
        <div className="w-[300px] flex-shrink-0 border-r border-border overflow-y-auto p-4 space-y-4">
          <h2 className="font-heading text-body-sm font-semibold text-text-primary">Screen A Controls</h2>

          {/* World Indicators */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-body text-[10px] font-semibold text-text-secondary uppercase tracking-wider">World Indicators</h3>
              <div className="flex gap-1">
                <button onClick={handleWiGenerate} disabled={wiLoading}
                  className="font-body text-[10px] font-medium bg-warning/10 text-warning px-2 py-0.5 rounded hover:bg-warning/20 disabled:opacity-40 transition-colors">
                  {wiLoading ? '...' : 'LLM'}
                </button>
                <button onClick={handleWiPublish} disabled={wiLoading}
                  className="font-body text-[10px] font-medium bg-success/10 text-success px-2 py-0.5 rounded hover:bg-success/20 disabled:opacity-40 transition-colors">
                  Publish
                </button>
              </div>
            </div>
            {wiDraft && (
              <div className="space-y-1.5">
                {([
                  ['Tension', 'geopolitical_tension', 'outlook_tension'],
                  ['Economy', 'economic_crisis', 'outlook_crisis'],
                  ['Doomsday', 'doomsday_clock', 'outlook_doom'],
                  ['AGI', 'distance_to_agi', 'outlook_agi'],
                ] as const).map(([label, levelKey, outlookKey]) => (
                  <div key={levelKey} className="flex items-center gap-1.5">
                    <span className="font-body text-caption text-text-secondary w-16 truncate">{label}</span>
                    <input type="number" min={1} max={10}
                      value={wiDraft[levelKey as keyof WiDraft] as number}
                      onChange={e => handleWiSave(levelKey, Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                      className="font-data text-caption bg-card border border-border rounded w-10 px-1 py-0.5 text-text-primary text-center focus:outline-none focus:border-action" />
                    {(['improving', 'stable', 'deteriorating'] as const).map(dir => (
                      <button key={dir}
                        onClick={() => handleWiSave(outlookKey, dir)}
                        className={`text-sm w-5 h-5 rounded flex items-center justify-center transition-colors ${
                          wiDraft[outlookKey as keyof WiDraft] === dir
                            ? dir === 'improving' ? 'bg-success/20 text-success'
                              : dir === 'deteriorating' ? 'bg-danger/20 text-danger'
                              : 'bg-text-secondary/20 text-text-primary'
                            : 'text-text-muted hover:text-text-secondary'
                        }`} title={dir}>
                        {dir === 'improving' ? '\u2198' : dir === 'deteriorating' ? '\u2197' : '\u2192'}
                      </button>
                    ))}
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <span className="font-body text-caption text-text-secondary">Col</span>
                  <input type="number" min={0} max={100}
                    value={wiDraft.power_columbia}
                    onChange={e => handleWiSave('power_columbia', Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="font-data text-caption bg-card border border-border rounded w-12 px-1 py-0.5 text-text-primary text-center focus:outline-none focus:border-action" />
                  <span className="font-body text-caption text-text-secondary">Cat</span>
                  <input type="number" min={0} max={100}
                    value={wiDraft.power_cathay}
                    onChange={e => handleWiSave('power_cathay', Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="font-data text-caption bg-card border border-border rounded w-12 px-1 py-0.5 text-text-primary text-center focus:outline-none focus:border-action" />
                </div>
                {wiDraft.status && (
                  <span className={`inline-block font-data text-[10px] px-2 py-0.5 rounded ${
                    wiDraft.status === 'published' ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'
                  }`}>
                    {wiDraft.status === 'published' ? `Published R${wiDraft.round_num}` : 'Draft'}
                  </span>
                )}
              </div>
            )}
            {!wiDraft && (
              <p className="font-body text-[10px] text-text-secondary/40 italic">No indicators yet. Click LLM to generate.</p>
            )}
          </div>

          {/* Broadcast */}
          <div className="space-y-1.5">
            <h3 className="font-body text-[10px] font-semibold text-text-secondary uppercase tracking-wider">News Ticker</h3>
            <div className="flex gap-1.5">
              <input type="text" value={broadcastText} onChange={e => setBroadcastText(e.target.value)}
                placeholder="Broadcast..."
                className="flex-1 bg-card border border-border rounded px-2 py-1.5 font-body text-caption text-text-primary placeholder:text-text-secondary/40 focus:outline-none focus:border-action"
                onKeyDown={e => { if (e.key === 'Enter' && broadcastText.trim()) handleBroadcast() }} />
              <button onClick={handleBroadcast} disabled={!broadcastText.trim()}
                className="font-body text-caption font-medium bg-accent/10 text-accent px-2.5 py-1.5 rounded hover:bg-accent/20 disabled:opacity-30 transition-colors">
                Send
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Review Results (when pending) or Screen B editor ── */}
        <div className="flex-1 overflow-y-auto p-4">
          <h2 className="font-heading text-body-sm font-semibold text-text-primary mb-2">Screen B — Round Events</h2>
          <ScreenBEditor simId={simId} />

          {/* Speech Recording */}
          <div className="mt-6 pt-4 border-t border-border">
            <SpeechRecorder simId={simId} />
          </div>

          {/* Preview — below all management elements */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-body text-[10px] text-text-secondary/60 uppercase tracking-wider">Preview:</span>
              {(['a', 'b', 'none'] as const).map(opt => (
                <button key={opt}
                  onClick={() => setPreviewScreen(opt)}
                  className={`font-body text-[11px] px-2.5 py-0.5 rounded transition-colors ${
                    previewScreen === opt
                      ? 'bg-action/15 text-action font-medium'
                      : 'text-text-secondary/50 hover:text-text-secondary'
                  }`}
                >
                  {opt === 'none' ? 'Hide' : `Screen ${opt.toUpperCase()}`}
                </button>
              ))}
              {previewScreen !== 'none' && (
                <button
                  onClick={() => window.open(previewScreen === 'b' ? `/screen/${simId}?screen=b` : `/screen/${simId}`, '_blank')}
                  className="ml-auto font-body text-[10px] text-action hover:text-action/80 transition-colors"
                >
                  Full Screen ↗
                </button>
              )}
            </div>
            {previewScreen !== 'none' && (
              <div className="bg-[#0A0E1A] border border-border rounded overflow-hidden" style={{ maxWidth: '900px' }}>
                <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <iframe
                    key={previewScreen}
                    src={previewScreen === 'b' ? `/screen/${simId}?screen=b` : `/screen/${simId}`}
                    className="absolute top-0 left-0 border-0 pointer-events-none origin-top-left"
                    style={{ width: '1920px', height: '1080px' }}
                    title={`Screen ${previewScreen.toUpperCase()}`}
                    ref={(el) => {
                      if (!el) return
                      const fit = () => {
                        const box = el.parentElement
                        if (!box) return
                        el.style.transform = `scale(${box.clientWidth / 1920})`
                      }
                      fit()
                      window.addEventListener('resize', fit)
                      el.addEventListener('load', fit)
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
